import { defineStore } from 'pinia'
import { SETTLEMENT_TAB_LABELS } from 'src/config/constants'
import type { IFamily } from 'src/interfaces/family'
import type { IPaymentGroup, ISettlement } from 'src/interfaces/payment'
import type { IProcessing, IProcessingBilling } from 'src/interfaces/processing'
import type { IStaff } from 'src/interfaces/staff'

export interface IDateRange {
  from: string
  to: string
}

export interface IAggState {
  isCreatingNewFamily: boolean
  families: IFamily[]
  familyTotal: number
  familyNextOffset: number
  staff: IStaff[]
  staffTotal: number
  staffNextOffset: number
  processing: IProcessing[]
  processingSelectedMonth: number
  processingSelectedYear: number
  processingSelectedRange: IDateRange
  processingFamilyId: string
  settlement: ISettlement[]
  settlementSelectedMonth: number
  settlementSelectedYear: number
  settlementSelectedRange: IDateRange
  settlementSelectedTab: string
  paymentGroups: IPaymentGroup[]
  paymentGroupsSelectedMonth: number
  paymentGroupsSelectedYear: number
  paymentGroupsSelectedRange: IDateRange
  paymentGroupsSelectedTab: string
}

export const useAggStore = defineStore('pinia/agg', {
  state: (): IAggState => {
    return {
      isCreatingNewFamily: false,
      families: [],
      familyTotal: 0,
      familyNextOffset: 50,
      staff: [],
      staffTotal: 0,
      staffNextOffset: 500,
      processing: [],
      processingSelectedMonth: new Date().getMonth() + 1,
      processingSelectedYear: new Date().getFullYear(),
      processingSelectedRange: { from: '', to: '' },
      processingFamilyId: '',
      settlement: [],
      settlementSelectedMonth: new Date().getMonth() + 1,
      settlementSelectedYear: new Date().getFullYear(),
      settlementSelectedRange: { from: '', to: '' },
      settlementSelectedTab: SETTLEMENT_TAB_LABELS.processed.value,
      paymentGroups: [],
      paymentGroupsSelectedMonth: new Date().getMonth() + 1,
      paymentGroupsSelectedYear: new Date().getFullYear(),
      paymentGroupsSelectedRange: { from: '', to: '' },
      paymentGroupsSelectedTab: SETTLEMENT_TAB_LABELS.processed.value,
    }
  },
  actions: {
    setIsCreatingNewFamily(isCreatingNewFamily: boolean) {
      this.isCreatingNewFamily = isCreatingNewFamily
    },
    appendFamilies(families: IFamily[]) {
      this.families = this.families.concat(families)
    },
    setFamilies(families: IFamily[]) {
      this.families = families
    },
    setFamilyTotal(familyTotal: number) {
      this.familyTotal = familyTotal
    },
    setFamilyNextOffset(familyNextOffset: number) {
      this.familyNextOffset = familyNextOffset
    },
    setStaffSingle(staff: IStaff) {
      const index = this.staff.findIndex((s) => s.id === staff.id)
      if (index || index === 0) this.staff[index] = { ...staff }
    },
    appendStaff(staff: IStaff[]) {
      this.staff = this.staff.concat(staff)
    },
    setStaff(staff: IStaff[]) {
      this.staff = staff
    },
    setStaffTotal(staffTotal: number) {
      this.staffTotal = staffTotal
    },
    setStaffNextOffset(staffNextOffset: number) {
      this.staffNextOffset = staffNextOffset
    },
    setProcessing(processing: IProcessing[]) {
      this.processing = processing
    },
    setProcessingSelectedMonth(month: number) {
      this.processingSelectedMonth = month
    },
    setProcessingSelectedYear(year: number) {
      this.processingSelectedYear = year
    },
    setProcessingSelectedRange(range: IDateRange) {
      this.processingSelectedRange = range
    },
    setProcessingFamilyId(familyId: string) {
      this.processingFamilyId = familyId
    },
    setSettlement(settlement: ISettlement[]) {
      settlement.forEach((settlement: ISettlement) => {
        if (settlement.payments) {
          settlement.payments[0].actual = settlement.payments[0].amount
        }
      })
      this.settlement = settlement
    },
    setSettlementSelectedMonth(month: number) {
      this.settlementSelectedMonth = month
    },
    setSettlementSelectedYear(year: number) {
      this.settlementSelectedYear = year
    },
    setSettlementSelectedTab(tab: string) {
      this.settlementSelectedTab = tab
    },
    setSettlementSelectedRange(range: IDateRange) {
      this.settlementSelectedRange = range
    },
    setPaymentGroups(paymentGroups: IPaymentGroup[]) {
      this.paymentGroups = paymentGroups
    },
    setPaymentGroupsSelectedMonth(month: number) {
      this.paymentGroupsSelectedMonth = month
    },
    setPaymentGroupsSelectedYear(year: number) {
      this.paymentGroupsSelectedYear = year
    },
    addBillingToProcessing({
      billing,
      registrationId,
    }: {
      billing: IProcessingBilling
      registrationId: string
    }) {
      let index = -1
      if (!billing.isCustom) {
        index = this.processing.findIndex(
          (registration) =>
            registration.registrationId === registrationId &&
            !registration.billings.some((b) => b.isCustom)
        )
      } else {
        index = this.processing.findIndex(
          (registration) =>
            registration.registrationId === registrationId &&
            registration.billings.every((b) => b.isCustom)
        )
      }
      if (index !== -1) {
        if (this.processing[index].billings)
          this.processing[index].billings.push(billing)
        else this.processing[index].billings = [billing]
      }
    },
    removeBillingFromProcessing(billing: IProcessingBilling) {
      const index = this.processing.findIndex((p) =>
        p.billings.some((b) => b.id === billing.id)
      )
      if (!billing.registrationId || billing.isCustom) {
        this.processing.splice(index, 1)
      } else {
        this.processing[index].billings = this.processing[
          index
        ].billings.filter((b) => b.id !== billing.id)
      }
    },
    replaceBilling(billing: IProcessingBilling) {
      const index = this.processing.findIndex((p) =>
        p.billings.map((b) => b.id).includes(billing.id)
      )
      if (index < 0) return
      const processing = this.processing[index]
      const billingIndex = processing.billings.findIndex(
        (b) => b.id === billing.id
      )
      if (billingIndex < 0) return
      processing.billings[billingIndex] = billing
    },
    updateBillingInProcessing({
      billing,
      registrationId,
    }: {
      billing: IProcessingBilling
      registrationId: string
    }) {
      let index = -1
      if (!registrationId) {
        index = this.processing.findIndex((p) =>
          p.billings.map((b) => b.id).includes(billing.id)
        )
      } else if (!billing.isCustom) {
        index = this.processing.findIndex(
          (registration) =>
            registration.registrationId === registrationId &&
            !registration.billings.some((b) => b.isCustom)
        )
      } else {
        index = this.processing.findIndex(
          (registration) =>
            registration.registrationId === registrationId &&
            registration.billings.every((b) => b.isCustom)
        )
      }
      if (index !== -1) {
        const billings = this.processing[index].billings
        const billingIndex = billings.findIndex((b) => b.id === billing.id)
        this.processing[index].billings[billingIndex] = billing
      }
    },
    addProcessing(processing: IProcessing) {
      this.processing.push(processing)
    },
    setPaymentGroupsAndSelectedMonthAndYear({
      paymentGroups,
      month,
      year,
    }: {
      paymentGroups: IPaymentGroup[]
      month: number
      year: number
    }) {
      this.setPaymentGroups(paymentGroups)
      this.setPaymentGroupsSelectedMonth(month)
      this.setPaymentGroupsSelectedYear(year)
    },
    updateMemberAvatar({
      familyId,
      memberId,
      url,
    }: {
      familyId: string
      memberId: string
      url: string
    }) {
      const family = this.families.find((family) => family.id === familyId)
      if (!family) return
      const member = family.members.find((member) => member.id === memberId)
      if (member) {
        member.avatar = url
      }
    },
    updateStudentAvatar({
      familyId,
      studentId,
      url,
    }: {
      familyId: string
      studentId: string
      url: string
    }) {
      const family = this.families.find((family) => family.id === familyId)
      if (family) {
        const student = family?.students.find(
          (student) => student.id === studentId
        )
        if (student) {
          student.avatar = url
        }
      }

      const processings = this.processing.filter(
        (processing) =>
          processing.familyId === familyId &&
          processing.student.id === studentId
      )
      for (const processing of processings) {
        processing.student.avatar = url
      }
    },
    removeStudentFromFamily({
      familyId,
      studentId,
    }: {
      familyId: string
      studentId: string
    }) {
      const family = this.families.find((family) => family.id === familyId)

      if (!family) return

      family.students = family.students.filter(
        (student) => student.id !== studentId
      )
    },
    removeMemberFromFamily({
      familyId,
      memberId,
    }: {
      familyId: string
      memberId: string
    }) {
      const family = this.families.find((family) => family.id === familyId)

      if (!family) return

      family.members = family.members.filter((member) => member.id !== memberId)
    },
    setFamiliesAndTotal({
      result,
      total,
      nextOffset,
      isReplace,
    }: {
      result: IFamily[]
      total: number
      nextOffset: number
      isReplace: boolean
    }) {
      if (isReplace) {
        this.setFamilies(result)
      } else {
        this.appendFamilies(result)
      }
      this.familyNextOffset = nextOffset
      this.familyTotal = total
    },
    setStaffAndTotal({
      result,
      total,
      nextOffset,
      isReplace,
    }: {
      result: IStaff[]
      total: number
      nextOffset: number
      isReplace: boolean
    }) {
      if (isReplace) {
        this.setStaff(result)
      } else {
        this.appendStaff(result)
      }
      this.setStaffNextOffset(nextOffset)
      this.setStaffTotal(total)
    },
    setProcessingAndSelectedMonthAndYear({
      processing,
      month,
      year,
      range,
      familyId,
    }: {
      processing: IProcessing[]
      month: number
      year: number
      range: IDateRange
      familyId: string
    }) {
      this.setProcessing(processing)
      this.setProcessingSelectedMonth(month)
      this.setProcessingSelectedYear(year)
      this.setProcessingSelectedRange(range)
      this.setProcessingFamilyId(familyId)
    },
    setSettlementAndSelectedMonthAndYear({
      settlement,
      month,
      year,
      range,
      tab,
    }: {
      settlement: ISettlement[]
      month: number
      year: number
      range: IDateRange
      tab: string
    }) {
      this.setSettlement(settlement)
      this.setSettlementSelectedMonth(month)
      this.setSettlementSelectedYear(year)
      this.setSettlementSelectedRange(range)
      this.setSettlementSelectedTab(tab)
    },
  },
})
